import { motion } from 'framer-motion'
import styled, { css } from 'styled-components/macro'

import { Overlay, styleReset } from 'styles/misc'
import { mq } from 'styles/mq'
import { theme } from 'styles/theme'
import { typography } from 'styles/typography'
import CardBorder from './images/quiz-card.svg'
import CardBorderMobile from './images/quiz-card-mobile.svg'
import { Section } from 'components/section'

export const StyledSection = styled(Section)`
  overflow: hidden;
`

// Because CMS does not allow for a proper CSS imports,
// we need to use !important here to override default Swiper CSS

export const Cards = styled.div`
  --swiper-navigation-sides-offset: -12rem;
  --nav-button-size: 4rem;

  position: relative;
  width: 100%;
  max-width: 48rem;
  margin: 0 auto;

  .swiper-slide {
    display: flex;
    flex-direction: column;
    height: auto;
  }

  .swiper-slide-shadow {
    background-color: rgb(255 255 255 / 80%) !important;
  }

  .swiper-slide-active ~ .swiper-slide .swiper-slide-shadow {
    opacity: 0.4 !important;
    background-color: white !important;
  }

  .swiper-slide-next + .swiper-slide .swiper-slide-shadow {
    opacity: 0.6 !important;
  }

  .swiper-slide-next + .swiper-slide + .swiper-slide .swiper-slide-shadow {
    opacity: 0.75 !important;
  }

  .swiper-slide-next
    + .swiper-slide
    + .swiper-slide
    ~ .swiper-slide
    .swiper-slide-shadow {
    opacity: 0.85 !important;
  }
`

export const NavigationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  pointer-events: none;
  margin: 1rem auto 0;
  width: 17rem;
  z-index: 10;

  ${mq.medium} {
    position: absolute;
    top: var(--swiper-navigation-top-offset, 50%);
    left: var(--swiper-navigation-sides-offset);
    right: var(--swiper-navigation-sides-offset);
    margin: 0;
    width: auto;
  }
`

export const NavButton = styled.button<{ $dir: 'next' | 'prev' }>`
  ${styleReset}
  width: var(--nav-button-size);
  height: var(--nav-button-size);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;

  &::after {
    content: '';
    display: block;
    width: inherit;
    height: inherit;
    background: url("data:image/svg+xml,%3Csvg width='41' height='41' viewBox='0 0 41 41' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4.01229 3.23265C3.39955 3.78045 3.18351 4.64589 3.46867 5.41672L9.20289 20.8701L3.46905 36.3225C3.19796 37.0566 3.38005 37.8797 3.92922 38.4289L4.01251 38.5065C4.31173 38.774 4.68461 38.945 5.08252 38.9974C5.48043 39.0498 5.88489 38.981 6.24315 38.8001L38.1885 22.6522C38.8591 22.312 39.2841 21.6216 39.2856 20.8691C39.287 20.1166 38.8592 19.4319 38.1886 19.086L6.24153 2.94033C5.88379 2.75873 5.47952 2.68947 5.08171 2.74163C4.68391 2.79379 4.31114 2.96495 4.01229 3.23265Z' fill='%232E3A59' stroke='%232E3A59' stroke-width='2'/%3E%3C/svg%3E%0A")
      center center / contain no-repeat;
  }

  &:not(:disabled) {
    cursor: pointer;
  }

  &:disabled {
    &::after {
      background: url("data:image/svg+xml,%3Csvg width='41' height='41' viewBox='0 0 41 41' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4.01229 3.23265C3.39955 3.78045 3.18351 4.64589 3.46867 5.41672L9.20289 20.8701L3.46905 36.3225C3.19796 37.0566 3.38005 37.8797 3.92922 38.4289L4.01252 38.5065C4.31173 38.774 4.68461 38.945 5.08252 38.9974C5.48043 39.0498 5.88489 38.981 6.24315 38.8001L38.1885 22.6522C38.8591 22.312 39.2841 21.6216 39.2856 20.8691C39.287 20.1166 38.8592 19.4319 38.1886 19.086L6.24153 2.94033C5.88379 2.75873 5.47952 2.68947 5.08171 2.74163C4.68391 2.79379 4.31114 2.96495 4.01229 3.23265Z' stroke='%232E3A59' stroke-width='2'/%3E%3C/svg%3E%0A")
        center center / contain no-repeat;
    }
  }

  ${(props) =>
    props.$dir === 'prev' &&
    css`
      transform: rotate(180deg);
    `}
`

export const Card = styled.div`
  --card-radius: 7rem;
  --border-width: 2px;

  display: flex;
  flex-direction: column;
  border-radius: var(--card-radius);
  overflow: hidden;
  flex-grow: 1;
  border: var(--border-width) solid currentColor;
  width: 100%;
  height: calc(90svh - var(--nav-button-size));

  ${mq.smallOnly} {
    max-height: 70rem;
  }

  ${mq.medium} {
    --card-radius: 10rem;
    height: auto;
  }

  .swiper-slide-active & {
    --border-width: 3px;
  }
`

export const Inner = styled(motion.div)`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: calc(3.2rem - var(--border-width)) calc(3.5rem - var(--border-width));
  border-radius: var(--card-radius);
  border-image: url('${CardBorderMobile}') 152 222 138 216 / 152px 222px 138px
    216px / 0 stretch;
  background-color: ${theme.colors.secondary};

  ${mq.medium} {
    aspect-ratio: 3 / 4;
    max-height: 90svh;
    border-image-source: url('${CardBorder}');
    padding: calc(5rem - var(--border-width));
  }
`

export const Title = styled(motion.h3)`
  ${typography.h3}
  margin: 0 0 2.4rem;
`

export const Instructions = styled(motion.p)`
  margin: 0;
`

export const Options = styled(motion.ul)`
  position: relative;
  margin: 0 0 2rem;
  padding: 0;
  border-radius: 2.7rem;
  box-shadow:
    0 6.2rem 4.2rem -4.2rem rgb(from ${theme.colors.primary} r g b / 20%),
    0 2.5rem 2.4rem -2.7rem rgb(from ${theme.colors.primary} r g b / 40%);
  overflow: hidden;
  background-color: ${theme.colors.background};
`

export const Option = styled(motion.li)`
  list-style: none;
  padding: 3px;

  &:not(:last-child)::after {
    content: '';
    display: block;
    border-top: 1px dashed currentColor;
    opacity: 0.2;
    margin: 3px 0 -3px 2.8rem;
  }

  input {
    width: 1px;
    height: 1px;
    position: absolute;
  }
`

export const RadioInput = styled.input.attrs({ type: 'radio' })`
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0.01;
`

export const Label = styled.span<{
  $highlighted: boolean
  $condensed?: boolean
}>`
  ${typography.strong}
  display: block;
  position: relative;
  padding: calc(1.4rem - 3px) calc(2rem - 3px);
  border-radius: 2.4rem 0 0 2.4rem;
  transition:
    border-radius 0.1s,
    color 0.2s,
    background-color 0.4s;
  background-color: ${theme.colors.background};

  ${mq.medium} {
    padding: calc(1.8rem - 3px) calc(2.8rem - 3px);
  }

  ${Option}:first-child & {
    border-top-left-radius: 2.4rem;
    border-top-right-radius: 2.4rem;
  }

  ${Option}:last-child & {
    border-bottom-left-radius: 2.4rem;
    border-bottom-right-radius: 2.4rem;
  }

  ${RadioInput}:not(:disabled) + & {
    cursor: pointer;
  }

  ${RadioInput}:not(:disabled) + &:hover,
  ${RadioInput}:focus-visible + & {
    background-color: ${theme.colors.secondary};
  }

  &::before,
  &::after {
    transition: border-radius 0.1s;
  }

  ${RadioInput}:checked + &::before {
    ${Overlay}
    content: "";
    display: block;
    border: 6px solid ${theme.colors.background};
    border-radius: inherit;
  }

  ${RadioInput}:checked + &::after {
    ${Overlay}
    content: "";
    display: block;
    border: 3px solid ${theme.colors.primary};
    border-radius: inherit;
  }

  ${(props) =>
    props.$highlighted &&
    css`
      color: white;
      background-color: ${theme.colors.success};
    `}

  ${(props) =>
    props.$condensed &&
    css`
      font-family: ${theme.fonts.headings};
      font-size: 0.9em;
    `}
`

export const SlideCount = styled.div`
  ${typography.headings};
  position: absolute;
  bottom: 0.8rem;
  left: 0;
  right: 0;
  text-align: center;
  pointer-events: none;
  font-size: max(${15 / 18}em, 1.5rem);
  color: ${theme.colors.text};
  z-index: 500;

  ${mq.medium} {
    bottom: 2rem;
  }
`

export const Description = styled(motion.div)`
  ${typography.box};
  position: relative;
  flex-grow: 1;
  height: 0;
  font-size: 1.5rem;
  overflow: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: contain;
`
